import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, ChevronDown, ChevronUp, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/remote-print`;

export default function RemotePrintSettings({ canEdit }) {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ printer_email: '', printer_name: '', poll_minutes: 3 });
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => { setS(r.data); setForm({ printer_email: r.data.printer_email || '', printer_name: r.data.printer_name || '', poll_minutes: r.data.poll_minutes || 3 }); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API}/settings`, form); setS(r.data); toast.success('Office printer saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (!s) return null;
  const configured = !!s.printer_email;
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white/70 backdrop-blur p-4" data-testid="remote-print-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Printer className="h-5 w-5 text-indigo-600" />
          <div>
            <p className="font-semibold text-slate-900">Office printer (email-to-print)</p>
            <p className="text-xs text-slate-600">
              {configured ? <>Timesheets sent from Field Tracker are emailed to <b>{s.printer_email}</b>{s.printer_name ? ` (${s.printer_name})` : ''}.</> : 'Not set up — "Send to office printer" won\'t work until the printer\'s mailbox is entered here.'}
              {s.last_job && <> Last sent: {s.last_job.label} by {s.last_job.by}, {new Date(s.last_job.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.</>}
            </p>
          </div>
        </div>
        <button onClick={() => setShowHelp(v => !v)} className="text-xs text-indigo-700 inline-flex items-center gap-1" data-testid="remote-print-help-toggle">
          Printer setup steps {showHelp ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>
      {canEdit && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-2 items-end">
          <label className="text-xs text-slate-700">Printer mailbox
            <input value={form.printer_email} onChange={e => setForm({ ...form, printer_email: e.target.value })} placeholder="print@365isi.com"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="remote-print-email" />
          </label>
          <label className="text-xs text-slate-700">Printer name <span className="text-slate-500">(optional)</span>
            <input value={form.printer_name} onChange={e => setForm({ ...form, printer_name: e.target.value })} placeholder="Kyocera TASKalfa 3554ci — front office"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="remote-print-name" />
          </label>
          <label className="text-xs text-slate-700">Printer checks mail every
            <div className="mt-1 flex items-center gap-1"><input type="number" min={1} max={60} value={form.poll_minutes} onChange={e => setForm({ ...form, poll_minutes: e.target.value })}
              className="w-16 border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="remote-print-poll" /><span className="text-xs text-slate-500">min</span></div>
          </label>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-1" data-testid="remote-print-save">
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {showHelp && (
        <div className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5" data-testid="remote-print-help">
          <p className="font-semibold text-slate-900">One-time setup on the Kyocera TASKalfa (Command Center RX at its IP, e.g. http://10.10.10.7 → Login as Admin):</p>
          <ol className="list-decimal ml-4 space-y-1">
            <li>Create a mailbox for the printer in Google Workspace, e.g. <b>print@365isi.com</b>. In that account turn on 2-Step Verification, then create an <b>App password</b> (Google Account → Security → App passwords) and enable <b>POP</b> (Gmail → Settings → Forwarding and POP/IMAP → Enable POP for all mail).</li>
            <li>Printer: <b>Function Settings → E-mail → POP3</b>: POP3 Protocol <b>On</b> · Check Interval <b>3 min</b> · Run once now. <b>User 1</b>: E-mail address print@365isi.com · POP3 Server <b>pop.gmail.com</b> · Port <b>995</b> · SSL/TLS <b>On</b> · Login user name print@365isi.com · Password = the app password · Delete e-mail after retrieval <b>On</b> · Cover Page <b>Off</b> (prints only the PDF, not the message text).</li>
            <li>Printer: <b>Function Settings → Printer</b>: make sure PDF direct printing is allowed (it is by default on the 3554ci). Optional: set the e-mail size limit high enough for multi-page sheets (≥ 5000 KB).</li>
            <li>Enter the same address above and Save. Then open Field Tracker → Timesheets → <b>Send to office printer</b> — the sheet shows up within the check interval.</li>
          </ol>
          <p className="text-slate-500">Nothing on the office network is reached directly: Ops emails the PDF, the printer fetches it. If a sheet doesn't print, check the printer's mailbox in Gmail — if the message is still there, the printer isn't polling (POP3 off / wrong password).</p>
        </div>
      )}
    </div>
  );
}
